import React, { useCallback, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility";
import JobStatus from "../../../Modal/JobStatus/JobStatus";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import LocationUpdate from "../../../Modal/LocationUpdate/LocationUpdate";
import DesignationEditModal from "../../../Modal/DesignationEditModal/DesignationEditModal";

function UserDetails() {
  const { state: userData = {} } = useLocation();

  console.log({ userData })
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const { role: userRole = "user", referralCode, fullName } = userData;
  const [currentRole, setCurrentRole] = useState(userRole);
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [error, setError] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [referredUsersLoading, setReferredUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit, setLimit] = useState(10);
  // Job status modal state
  const [showJobStatusModal, setShowJobStatusModal] = useState(false);
  const [selectedJobStatus, setSelectedJobStatus] = useState("");
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [showLocationUpdateModal, setShowLocationUpdateModal] = useState(false);
  const [showDesignationEditModal, setShowDesignationEditModal] = useState(false);
  // Helper functions with useCallback for optimization
  const getRoleBadgeColor = useCallback((role) => {
    const roleColors = {
      admin: "danger",
      vendor: "primary",
      user: "primary",
      agent: "success",
      insurer: "warning",
    };
    return roleColors[role?.toLowerCase()] || "secondary";
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  const handleChangeRole = async (targetRole) => {
    const action =
      targetRole === "agent" ? "make an agent" : "remove from agent role";
    const userName = fullName || "this user";

    // Show confirmation toast instead of browser confirm
    const confirmToast = toast.info(
      <div>
        <p className="mb-2">
          Are you sure you want to {action} <strong>{userName}</strong>?
        </p>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              toast.dismiss(confirmToast);
              proceedWithRoleChange(targetRole);
            }}
          >
            Yes, Continue
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => toast.dismiss(confirmToast)}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
      }
    );
  };

  // Separate function to handle the actual role change
  const proceedWithRoleChange = async (targetRole, jobStatus) => {
    const action = targetRole === "agent" ? "agent" : "user";
    const userName = fullName || "user";
    setRoleLoading(true);
    setError(null);

    try {
      const payload = {
        userId: userData?._id,
        email: userData?.email,
        targetRole: targetRole,
      };
      if (jobStatus) {
        if (typeof jobStatus === "object") {
          if (jobStatus.jobStatus) {
            payload.jobStatus = jobStatus.jobStatus;
          }
          if (jobStatus.policyId) {
            payload.agentCreatedOnPolicy = jobStatus.policyId;
          }
        } else {
          payload.jobStatus = jobStatus;
        }
      }
      const response = await postData("api/users/change-to-agent", payload);

      if (response?.success) {
        setCurrentRole(targetRole);

        toast.success(`Successfully changed ${userName}'s role to ${action}!`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Navigate back after a short delay to allow user to see the success message
        // setTimeout(() => {
        //   navigate("/user-management", { replace: true });
        // }, 1500);
      } else {
        throw new Error(response?.message || "Failed to change role");
      }
    } catch (error) {
      console.error("Role change failed:", error);
      const errorMessage =
        error.message || "An unexpected error occurred while changing the role";

      // Show error toast
      toast.error(`Failed to change role: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setError(errorMessage);
    } finally {
      setRoleLoading(false);
      setShowRoleMenu(false);
    }
  };

  // Determine available role options based on current role
  const getRoleOptions = () => {
    if (currentRole === "agent") {
      return [
        {
          label: "Make User",
          value: "user",
          action: () => handleChangeRole("user"),
        },
      ];
    }
    return [
      {
        label: "Make Agent",
        value: "agent",
        action: () => handleMakeAgentWithJobStatus(),
      },
    ];
  };

  // Handler to open the JobStatus modal for 'Make Agent'
  const handleMakeAgentWithJobStatus = () => {
    setPendingRoleChange("agent");
    setShowJobStatusModal(true);
  };

  const fetchReferredUsers = async (page = currentPage) => {
    if (!referralCode) return;

    setReferredUsersLoading(true);
    try {
      const response = await postData("api/users/list", {
        search: referralCode,
        limit,
        page: page + 1,
      });
      if (response.success) {
        setReferredUsers(response.data.users);
        setCurrentPage((response.data.currentPage || 1) - 1);
        setTotalPages(response.data.totalPages);
        setTotalUsers(response.data.totalUsers);
      }
    } catch (error) {
      console.error("Error fetching referred users:", error);
    } finally {
      setReferredUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchReferredUsers();
  }, [referralCode]);

  const handleReferredUsersPageChange = ({ selected }) => {
    setCurrentPage(selected);
    fetchReferredUsers(selected);
  };

  // Navigate to user details page
  const handleViewDetails = (user) => {
    navigate("/user-details", { state: user });
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading || roleLoading || referredUsersLoading} />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="content">
        <div className="container-fluid">
          {/* Page Header */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">User Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">User Details</li>
                  <li className="breadcrumb-item active">
                    {userData.fullName || "N/A"}
                  </li>
                </ol>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
                disabled={loading}
                aria-label="Go Back"
              >
                <i className="fas fa-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>

          {/* User Profile Header */}
          <div className="card mb-4 shadow-sm">
            <div className="card-body d-flex flex-column flex-md-row align-items-center gap-3">
              {/* Profile info */}
              <div className="flex-grow-1 text-center text-md-start">
                <h4 className="mb-2">{userData.fullName || "N/A"}</h4>
                <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
                  <span
                    className={`badge bg-${getRoleBadgeColor(
                      userData.role
                    )} fs-6`}
                  >
                    {userData.role?.toUpperCase() || "N/A"}
                  </span>
                  {userData.role != "user" && (
                    <span
                      className="badge fs-6"
                      style={{ backgroundColor: "#1f5092" }}
                    >
                      Referral Code: {userData.referralCode || "N/A"}
                    </span>
                  )}
                  <span
                    className="badge fs-6"
                    style={{ backgroundColor: "#1f5092" }}
                  >
                    Unique ID: {userData.uniqueId || "N/A"}
                  </span>
                  <span
                    className="badge fs-6"
                    style={{ backgroundColor: "#1f5092" }}
                  >
                    Referral By: {userData.referredBy || "N/A"}
                  </span>
                </div>
              </div>

              {/* Change Role Dropdown */}
              <div className="mt-3 position-relative d-inline-block">
                <button
                  className="btn btn-outline-primary dropdown-toggle"
                  type="button"
                  id="roleDropdown"
                  onClick={() => setShowRoleMenu(!showRoleMenu)}
                  disabled={roleLoading}
                  aria-expanded={showRoleMenu}
                >
                  {roleLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Updating...
                    </>
                  ) : (
                    "Change Role"
                  )}
                </button>
                {showRoleMenu && (
                  <ul
                    className="dropdown-menu show"
                    aria-labelledby="roleDropdown"
                    style={{ minWidth: "8rem" }}
                  >
                    {getRoleOptions().map((option) => (
                      <li key={option.value}>
                        <button
                          className="dropdown-item"
                          onClick={option.action}
                          disabled={roleLoading}
                        >
                          {option.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {error && <div className="text-danger mt-2">{error}</div>}

                {/* Job Status Modal for Make Agent */}
                <JobStatus
                  show={showJobStatusModal}
                  onClose={() => {
                    setShowJobStatusModal(false);
                    setSelectedJobStatus("");
                    setPendingRoleChange(null);
                  }}
                  selectedJobStatus={selectedJobStatus}
                  onJobStatusChange={setSelectedJobStatus}
                  onConfirm={async (jobStatus) => {
                    setShowJobStatusModal(false);
                    await proceedWithRoleChange(pendingRoleChange, jobStatus);
                    setSelectedJobStatus("");
                    setPendingRoleChange(null);
                  }}
                  userData={userData}
                />
              </div>
              {userData.role === "agent" && userData.status === "active" && (
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate("/agent-role", { state: userData })}
                >
                  <i className="mdi mdi-account-plus me-2"></i>Add Role
                </button>
              )}
            </div>
          </div>

          {/* User Details Sections */}
          <div className="row">
            {/* Personal Information */}
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-id-card me-2"></i>Personal Information
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Email:</strong>
                        <div className="text-muted">
                          {userData.email || "N/A"}
                        </div>
                      </div>
                      {userData.email && (
                        <a
                          href={`mailto:${userData.email}`}
                          className="btn btn-sm btn-outline-secondary"
                          aria-label="Send Email"
                        >
                          <i className="fas fa-envelope"></i>
                        </a>
                      )}
                    </li>
                    <li className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Mobile:</strong>
                        <div className="text-muted">
                          {userData.mobile || "N/A"}
                        </div>
                      </div>
                      {userData.mobile && (
                        <a
                          href={`tel:${userData.mobile}`}
                          className="btn btn-sm btn-outline-secondary"
                          aria-label="Call User"
                        >
                          <i className="mdi mdi-phone"></i>
                        </a>
                      )}
                    </li>
                    <li className="list-group-item px-0 py-2">
                      <strong>Date of Birth:</strong>
                      <div className="text-muted">
                        {userData.dob ? formatDate(userData.dob) : "N/A"}
                      </div>
                    </li>
                    <li className="list-group-item px-0 py-2">
                      <strong>Gender:</strong>
                      <div className="text-muted">
                        {userData.gender || "Not specified"}
                      </div>
                    </li>
                    <li className="list-group-item px-0 py-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong>Designation:</strong>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setShowDesignationEditModal(true)}
                        >
                          <i className="mdi mdi-pencil"></i> Edit
                        </button>
                      </div>
                      <div className="text-muted">
                        {userData.designation || "Not specified"}
                      </div>
                    </li>
                    <li className="list-group-item px-0 py-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong>Location:</strong>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setShowLocationUpdateModal(true)}
                        >
                          <i className="mdi mdi-pencil"></i> Edit
                        </button>
                      </div>
                      <div className="text-muted">
                        {userData.location?.address || userData.location?.city || userData.location?.state || userData.location?.pincode ? (
                          <div className="d-flex flex-column">
                            {userData.location.address && (
                              <div>{userData.location.address}</div>
                            )}
                            <div className="d-flex gap-2 flex-wrap">
                              {userData.location.city && (
                                <span>{userData.location.city},</span>
                              )}
                              {userData.location.state && (
                                <span>{userData.location.state} -</span>
                              )}
                              {userData.location.pincode && (
                                <span>{userData.location.pincode}</span>
                              )}
                            </div>
                            <div className="text-muted small mt-1">
                              {userData.location.coordinates ? (
                                <span>
                                  Coordinates:{" "}
                                  {userData.location.coordinates[1]?.toFixed(6) || '0.000000'},{" "}
                                  {userData.location.coordinates[0]?.toFixed(6) || '0.000000'}
                                </span>
                              ) : (
                                <span className="text-warning">
                                  <i className="mdi mdi-alert-circle-outline me-1"></i>
                                  Location coordinates not available
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-warning">
                            <i className="mdi mdi-map-marker-off-outline me-1"></i>
                            No location information available
                          </div>
                        )}
                      </div>
                    </li>

                  </ul>
                </div>
              </div>
            </div>
            {/* Performance Metrics */}
            <div className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-chart-line me-2"></i>Performance
                    Metrics
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="list-group list-group-flush">
                    {userData.role != "user" && (
                      <li className="list-group-item px-0 py-2">
                        <strong>Term Plans Sold:</strong>
                        <div className="text-muted">
                          {userData.totalApprovedPolicies || 0}
                        </div>
                      </li>
                    )}
                    <li className="list-group-item px-0 py-2">
                      <strong>Member Since:</strong>
                      <div className="text-muted">
                        {formatDate(userData.createdAt)}
                      </div>
                    </li>
                    <li className="list-group-item px-0 py-2">
                      <strong>Last Updated:</strong>
                      <div className="text-muted">
                        {formatDate(userData.updatedAt)}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Referred Users */}
          {userData.role != "user" && (
            <div className="col-md-12 mb-4">
              <div className="card h-100">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-users me-2"></i>Referred Users
                  </h5>
                </div>
                <div className="card-body">
                  {referredUsersLoading ? (
                    <div className="text-center">Loading...</div>
                  ) : referredUsers.length > 0 ? (
                    <>
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th>Role</th>
                            <th>Unique ID</th>
                            <th>Join Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referredUsers
                            .filter(
                              (user) => user.uniqueId !== userData.uniqueId
                            )
                            .map((user) => (
                              <tr key={user._id}>
                                <td>{user.fullName}</td>
                                <td>{user.email}</td>
                                <td>{user.mobile}</td>
                                <td>{user.role}</td>
                                <td
                                  className="cursor-pointer text-primary text-underline"
                                  onClick={() => handleViewDetails(user)}
                                >
                                  {user.uniqueId || "N/A"}
                                </td>
                                <td>
                                  {new Date(
                                    user.createdAt
                                  ).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-info"
                                      onClick={() => handleViewDetails(user)}
                                      title="View Details"
                                    >
                                      <i className="mdi mdi-eye"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      title="Send Email"
                                      onClick={() => {
                                        const email = user.email;
                                        const subject =
                                          "Regarding your enquiry";
                                        const body =
                                          "Hello, I would like to know more!";
                                        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
                                          subject
                                        )}&body=${encodeURIComponent(body)}`;
                                        window.open(mailtoLink, "_blank");
                                      }}
                                    >
                                      <i className="mdi mdi-email"></i>
                                    </button>

                                    <button
                                      className="btn btn-sm btn-outline-info"
                                      title="View Details"
                                      onClick={() => {
                                        const phoneNumber = `91${user.mobile}`;
                                        const message =
                                          "Hello, I would like to know more!";
                                        const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                                          message
                                        )}`;
                                        window.open(whatsappURL, "_blank");
                                      }}
                                    >
                                      <i className="mdi mdi-whatsapp"></i>
                                    </button>
                                    <a
                                      href={`tel:${user.mobile}`}
                                      className="btn btn-sm btn-outline-info"
                                      title="Call User"
                                    >
                                      <i className="mdi mdi-phone"></i>
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          Showing {currentPage * limit + 1} to{" "}
                          {Math.min((currentPage + 1) * limit, totalUsers - 1)}
                          {" "}of {totalUsers - 1} users
                        </div>
                        {totalPages > 1 && (
                          <ReactPaginate
                            previousLabel={
                              <i className="mdi mdi-chevron-left"></i>
                            }
                            nextLabel={
                              <i className="mdi mdi-chevron-right"></i>
                            }
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            pageCount={totalPages}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handleReferredUsersPageChange}
                            containerClassName="pagination pagination-rounded justify-content-end mb-0"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center">No referred users found</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <LocationUpdate
        show={showLocationUpdateModal}
        onClose={() => setShowLocationUpdateModal(false)}
        initialLocation={userData || {}}
      />

      <DesignationEditModal
        show={showDesignationEditModal}
        onClose={() => setShowDesignationEditModal(false)}
        currentDesignation={userData.designation}
        userData={userData}
      />
    </div>
  );
}

export default UserDetails;
